import { Stack, Typography } from '@mui/material';
import type { Account, RestoreAccount } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback } from 'react';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { Loader } from '@/components/layout/loading';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { ReadMoreLink } from '@/components/shared/readMoreLink';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import type { projectDalFactory } from '@/data/dal/dalFactory';
import { getDocsUrl } from '@/docs/docsUrls';
import { getAccountProvider } from '@/utils/accounts';

import {
  AwsRestoreAccountConfigFlow,
  AzureRestoreAccountConfigFlow,
  GcpRestoreAccountConfigFlow,
  type RestoreAccountConfigState,
} from '../restoreAccountConfigFlow';

const onAwsSave = async (
  dal: ReturnType<typeof projectDalFactory>,
  state: RestoreAccountConfigState
) => {
  await dal.cloudAccounts.restore.connectivity.update(state.account.id, {
    aws: state.config.config.aws,
  });
};

const onGcpSave = async (
  dal: ReturnType<typeof projectDalFactory>,
  state: RestoreAccountConfigState
) => {
  await dal.cloudAccounts.restore.connectivity.update(state.account.id, {
    gcp: state.config.config.gcp,
  });
};

const onAzureSave = async (
  dal: ReturnType<typeof projectDalFactory>,
  state: RestoreAccountConfigState
) => {
  await dal.cloudAccounts.restore.connectivity.update(state.account.id, {
    azure: state.config.config.azure,
  });
};

const AWS_DESCRIPTION =
  'Restore operations run on a temporary server. Before creating it, Eon selects an availability zone and security group based on the restore destination and resource type. If your default VPC resources have outbound limitations or were deleted, you\u2019ll need to customize the connectivity.';

const GCP_DESCRIPTION =
  'Restore operations run on a temporary server. Before creating it, Eon selects a subnet based on the restore destination and resource type. If your default network resources have outbound limitations or were deleted, you\u2019ll need to customize the connectivity.';

const AZURE_DESCRIPTION =
  'Restore operations run on a temporary server. Before creating it, Eon selects a subnet in your configured VNet based on the restore destination. If no VNet is configured, Eon uses its own managed network.';

function getConfigFlow(cloudProvider: string) {
  switch (cloudProvider) {
    case Provider.Aws:
      return AwsRestoreAccountConfigFlow;
    case Provider.Azure:
      return AzureRestoreAccountConfigFlow;
    case Provider.Gcp:
      return GcpRestoreAccountConfigFlow;
    default:
      return GcpRestoreAccountConfigFlow;
  }
}

function getDescription(cloudProvider: string) {
  switch (cloudProvider) {
    case Provider.Aws:
      return AWS_DESCRIPTION;
    case Provider.Azure:
      return AZURE_DESCRIPTION;
    case Provider.Gcp:
      return GCP_DESCRIPTION;
    default:
      return GCP_DESCRIPTION;
  }
}

export function RestoreServerConnectivityPanel({
  account,
  onUpdate,
  onClose,
}: {
  account: Account | RestoreAccount;
  onUpdate: () => Promise<void>;
  onClose: () => void;
}) {
  const dal = useDAL();
  const {
    data: config,
    isLoading,
    mutate,
  } = dal.cloudAccounts.restore.connectivity.useConnectivity(account.id);
  const cloudProvider = getAccountProvider(account);

  const onSave = useCallback(
    async (state: RestoreAccountConfigState) => {
      if (cloudProvider === Provider.Aws) {
        await onAwsSave(dal, state);
      } else if (cloudProvider === Provider.Gcp) {
        await onGcpSave(dal, state);
      } else if (cloudProvider === Provider.Azure) {
        await onAzureSave(dal, state);
      }
      await onUpdate();
      await mutate();
      return true;
    },
    [dal, onUpdate, mutate, cloudProvider]
  );

  if (isLoading || !config) {
    return <Loader />;
  }

  return (
    <PanelWrapper
      testIdPrefix='restore-account-config'
      header={{
        title: 'Edit restore server connectivity',
        subtitle: (
          <Stack className='px-[20px]' gap='24px'>
            <CloudProviderIndicator
              cloudProvider={cloudProvider}
              text={account.providerAccountId}
            />
            <Stack gap='12px' className='mb-[24px]'>
              <Typography className='max-w-[680px]' variant='body1'>
                {getDescription(cloudProvider)}{' '}
                <ReadMoreLink
                  href={getDocsUrl('restoreAccountNetworkConnectivity')}
                />
              </Typography>
            </Stack>
          </Stack>
        ),
        onBack: onClose,
        onClose: onClose,
      }}
    >
      <Wizard<RestoreAccountConfigState>
        hideBackArrow={true}
        onAbort={onClose}
        onFinish={onSave}
        flow={[getConfigFlow(cloudProvider)]}
        initialState={{
          account,
          config: { ...config.restoreAccountConfig },
        }}
      />
    </PanelWrapper>
  );
}
